import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/Login/LoginPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import GestorPage from './pages/Gestor/GestorPage';
import ProjectDetailPage from './pages/Gestor/ProjectDetailPage';
import CreateProjectPage from './pages/Gestor/CreateProjectPage';
import ClientsPage from './pages/Clientes/ClientsPage';
import TemplatesPage from './pages/Plantillas/TemplatesPage';
import StockPage from './pages/Stock/StockPage';
import InvoicesPage from './pages/Facturas/InvoicesPage';
import EmbalajePage from './pages/Embalaje/EmbalajePage';
import ImprentaPage from './pages/Imprenta/ImprentaPage';
import DTFPage from './pages/DTF/DTFPage';
import ActivityLogPage from './pages/Admin/ActivityLogPage';
import UsersPage from './pages/Admin/UsersPage';
import MaquinasPage from './pages/Maquinas/MaquinasPage';
import MaterialConsumptionPage from './pages/Stock/MaterialConsumptionPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated());
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasRole } = useAuthStore();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!hasRole('ROLE_ADMIN')) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RoleRoute({ children, role }: { children: React.ReactNode; role: string }) {
  const { isAuthenticated, hasRole } = useAuthStore();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!hasRole(role) && !hasRole('ROLE_ADMIN')) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <MainLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="gestor" element={<GestorPage />} />
              <Route path="gestor/nuevo" element={<CreateProjectPage />} />
              <Route path="gestor/proyecto/:id" element={<ProjectDetailPage />} />
              <Route path="clientes" element={<ClientsPage />} />
              <Route path="plantillas" element={<TemplatesPage />} />
              <Route path="stock" element={<StockPage />} />
              <Route path="stock/consumo" element={<MaterialConsumptionPage />} />
              <Route path="financiero" element={
                <RoleRoute role="ROLE_FINANCIERO">
                  <InvoicesPage />
                </RoleRoute>
              } />
              <Route path="embalaje" element={<EmbalajePage />} />
              <Route path="imprenta" element={<ImprentaPage />} />
              <Route path="dtf" element={<DTFPage />} />
              <Route path="maquinas" element={<MaquinasPage />} />
              <Route path="usuarios" element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              } />
              <Route path="admin/actividad" element={
                <AdminRoute>
                  <ActivityLogPage />
                </AdminRoute>
              } />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}
